import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import style from './style';
import i18next from 'i18next';
import ContextAttributes from './steps/ContextAttributes';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getContextModel,
  saveContextInstance,
  saveContextInstanceThirdparty,
} from '../../../services/edge/ContexteService';
import { Context } from '../../../states/Store';
import { ERROR } from '../../../services/common/Constants';
import { useHistory } from 'react-router-dom';
import ROUTES from '../../../config/Routes';
import EAFormStepper from '../../../components/common/EAFormStepper';
import ThirdPartySearch from './components/ThirdPartySearch';
import ThirdPartyList from './components/ThirdPartyList';
import { useAlert } from '../../../components/common/hooks/useAlert';
import Add from './components/Add';
import EAButton from '../../../components/common/EAButton';
import StepperMainInfo from '../../../components/context/stepper/StepperMainInfo';
import ContextInfos from '../../../components/context/stepper/ContextInfos';
import AddedTpInfos from './components/AddedTpInfos';
import EAIcon from '../../../components/common/EAIcon';

const defaultTpValues = {
  tpInfos: null,
  thirdPartyId: null,
  contextInstanceId: null,
  parentId: -1,
  tiersAttributes: [],
  referents: [],
};

const AddContext = () => {
  const { classes } = style();
  const stepSetterRef = useRef();
  const currentStep = useRef();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const [selectedContextModel, setSelectedContextModel] = useState();
  const contextModels = state.contextModels;
  const [foundTp, setFoundTp] = useState(null);
  const [addedTpList, setAddedTpList] = useState([]);
  const { onSuccess } = useAlert();
  const [isEditingExistingTP, setIsEditingExistingTP] = useState(false);
  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      context: {
        contextModelName: '',
        description: '',
        reference: '',
        contextAttributes: [],
      },
      tp: defaultTpValues,
    },
  });
  const getStepSetter = (setter, step) => {
    stepSetterRef.current = setter;
    currentStep.current = step;
  };

  const { getValues,
    setValue,
    trigger,
    watch,
    formState
  } = formMethods;
  const tiersAttributes = watch('tp.tiersAttributes');
  const tpToAdd = watch('tp.tpInfos');
  const referents = watch('tp.referents');

  useEffect(() => {
    if (selectedContextModel) {
      setValue('tp.tiersAttributes', selectedContextModel.tiersAttributes);
      dispatch({
        type: 'IS_ASSOCIATING_CONTEXT_THIRDPARTY',
        isAssociatingContextThirdParty: true,
      });
    }
  }, [selectedContextModel, dispatch, setValue]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'IS_ASSOCIATING_CONTEXT_THIRDPARTY',
        isAssociatingContextThirdParty: false,
      });
    };
  }, [dispatch]);

  const fetchContextModels = useCallback(async () => {
    try {
      await getContextModel(state.account.id, dispatch);
      dispatch({
        type: 'IS_CREATING_CONTEXT_INSTANCE',
        isCreatingContextInstance: true,
      });
    } catch (e) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'INTERNAL_ERROR' },
      });
    }
  }, [state.account.id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'IS_CREATING_CONTEXT_INSTANCE',
        isCreatingContextInstance: false,
      });
    };
  }, [dispatch]);

  useEffect(() => {
    fetchContextModels();
  }, [state.account.id, fetchContextModels]);

  const close = () => {
    history.replace(`/${state.account.id}/${ROUTES.CONTEXT}`);
  };

  const createContext = async () => {
    const contextValues = getValues('context');
    try {
      const savedInstanceId = await saveContextInstance(
        state.account.id,
        contextValues
      );
      saveTiers(savedInstanceId);
    } catch (e) {
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: 'INTERNAL_ERROR' },
      });
    }
  };

  const onClose = useCallback((contextInstanceId) => {
    history.replace(
      `/${state.account.id}/${ROUTES.CONTEXT}?instance=${contextInstanceId}`
    );
  }, [state.account.id, history]);

  const saveTiers = useCallback(
    async (contextInstanceId) => {
      //switching attributeId to id because it's an attributeValue payload
      const payload = addedTpList.map(({ tpInfos, ...payloadInfos }) => ({
        ...payloadInfos,
        tiersAttributes: payloadInfos.tiersAttributes.map(t => ({
          ...t,
          attributeId: t.id,
          id: null,
        })),
        contextInstanceId,
      }));
      await saveContextInstanceThirdparty(state.account.id, payload, dispatch);
      onSuccess(i18next.t('notifications.notifications.associated'));
      onClose(contextInstanceId);
    },
    [state.account.id, addedTpList, dispatch, onClose, onSuccess]
  );

  const addTiers = useCallback(() => {
    setAddedTpList((prev) => [
      ...prev.filter((tp) => tp.thirdPartyId !== getValues('tp').thirdPartyId),
      getValues('tp'),
    ]);
    stepSetterRef.current(1);
    setFoundTp(null);
    setValue('tp', {
      ...defaultTpValues,
      tiersAttributes: selectedContextModel.tiersAttributes,
    });
  }, [selectedContextModel, getValues, setValue]);

  const editTp = useCallback(
    (tpToAdd) => {
      const existingTp = addedTpList.find(
        (tp) => tpToAdd.thirdpartyId === tp.thirdPartyId
      );

      if (existingTp) {
        setValue('tp', existingTp, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        setIsEditingExistingTP(true);
      } else {
        setValue('tp.thirdPartyId', tpToAdd.thirdpartyId);
        setValue('tp.tpInfos', tpToAdd);
        setValue('tp.tiersAttributes', selectedContextModel.tiersAttributes);
        setIsEditingExistingTP(false);
      }
      stepSetterRef.current(2);
    },
    [addedTpList, selectedContextModel, setValue]
  );

  const onEditAttribute = (data, attributeValue) => {
    const tiersAttributes = getValues('tp.tiersAttributes');

    setValue('tp.tiersAttributes', [
      ...tiersAttributes.map((att) => {
        if (att.id === attributeValue.id) {
          return { ...att, ...data };
        }
        return att;
      }),
    ]);
  };

  const addContact = (email) => {
    if(email.trim() === "") return
    const referents = getValues('tp.referents');
    setValue('tp.referents', [...new Set([...referents, email.trim()])]);
  };

  const deleteReferent = (email) => {
    const referents = getValues('tp.referents');
    setValue(
      'tp.referents',
      referents.filter((r) => r !== email)
    );
  };

  const deleteTp = useCallback(
    (tpIdToDelete) => {
      if(currentStep.current === 2) {
        stepSetterRef.current(1);
      }
      setAddedTpList((prev) =>
        prev.filter((tp) => tp.thirdPartyId !== tpIdToDelete)
      );
      setValue('tp', {
        ...defaultTpValues,
        tiersAttributes: selectedContextModel.tiersAttributes,
      });
    },
    [selectedContextModel, setValue]
  );

  const watchAttributes = watch('context.contextAttributes');

  useEffect(() => {
    watchAttributes.forEach((attribute, index) => {
      if (attribute.isRequired && (!attribute.values?.length || attribute.values === null || attribute.value === null)) {
        formMethods.setError(`attributes.${index}`, {
          type: 'manual',
          message: i18next.t("infos.required"),
          attributeId: attribute.id
        });
      } else {
        formMethods.clearErrors(`attributes.${index}`);
      }
    })
  }, [watchAttributes, formMethods]);

  const stepFieldMap = useMemo(() =>{
      return {
        0: ['context.reference', 'context.contextModelName', 'context.expirationDate', 'attributes'],
        1: ['contextAttributes'],
      };
  }, []);

  const checkValidStep = useCallback(async (stepIndex) => {
    const fields = stepFieldMap[stepIndex] || [];
    return await trigger(fields) && !Object.keys(formState.errors).length;
  }, [formState.errors, stepFieldMap, trigger]);

  const Infos = ({ title, subtitle, text, displayModifyButton = true }) => {
    return (
      <>
        <StepperMainInfo title={title} subtitle={subtitle} text={text} />
        <ContextInfos
          category={i18next.t('context.addTiers.context')}
          title={getValues('context').contextModelName}
          subtitle={getValues('context').reference}
          onEdit={() => stepSetterRef.current(0)}
          displayModifyButton={displayModifyButton}
        />
        <AddedTpInfos
          tpList={addedTpList}
          onDelete={deleteTp}
          onEdit={editTp}
        />
      </>
    );
  };

  const stepsContent = [
    {
      id: 'param',
      name: i18next.t('context.parametrage'),
      left: (
        <Infos
          title={i18next.t('context.creationInstance.stepper.title')}
          subtitle={i18next.t('context.creationInstance.stepper.subTitle')}
          text={i18next.t('context.creationInstance.stepper.text')}
          displayModifyButton={false}
        />
      ),
      right: (
        <ContextAttributes
          contextModels={contextModels}
          setSelectedContextModel={setSelectedContextModel}
          selectedContextModel={selectedContextModel}
          formSelector={'context'}
        />
      ),
    },
    {
      id: 'search',
      name: i18next.t('context.associationTiers'),
      left: (
        <Infos
          title={i18next.t('context.creationInstance.stepper.title')}
          subtitle={i18next.t('context.creationInstance.stepper.subTitle')}
          text={i18next.t('context.creationInstance.stepper.text')}
        />
      ),
      right: (
        <>
          <ThirdPartySearch setFoundTp={setFoundTp} />
          {foundTp&&<ThirdPartyList
            thirdPartyList={foundTp}
            editTp={editTp}
            addedTpList={addedTpList}
            existingAssosciated={[]}
            startPoint={1}
          />}
        </>
      ),
      customNextButton: (
          <EAButton
            onClick={async (e) => {
              e.stopPropagation();
              e.preventDefault();
              await createContext();
            }}
            size={'large'}
            content={<>
              {i18next.t('context.creationInstance.terminerCreation')}
              <EAIcon icon={'context/fatArrow'} style={{marginLeft:'12px'}}/>
            </>}
            customStyle={{
              position: 'absolute',
              width: 'auto',
              left: '22px',
              right: '22px',
              bottom: 30,
              padding:'0px 22px',
              borderRadius: '60px',
              zIndex: 2,
            }}
          />
      ),
    },
    {
      id: 'add',
      name: 'add',
      left: (
        <Infos
          title={i18next.t('context.creationInstance.stepper.title')}
          subtitle={i18next.t('context.creationInstance.stepper.subTitle')}
          text={i18next.t('context.creationInstance.stepper.text')}
        />
      ),
      right: (
        <Add
          tiersAttributes={tiersAttributes}
          tpToAdd={tpToAdd}
          onEditAttribute={onEditAttribute}
          addContact={addContact}
          deleteReferent={deleteReferent}
          referents={referents}
        />
      ),
      hideInBreadCrumb: true,
      customNextButton: (
        <div className={classes.customAddTiersButtons}>
          <EAButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              stepSetterRef.current(1);
            }}
            size={'large'}
            content={i18next.t(`Annuler`)}
            customStyle={{
              width: '50%',
              borderRadius: '60px',
              zIndex: 2,
            }}
          />
          <EAButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              addTiers();
            }}
            size={'large'}
            content={
              isEditingExistingTP
                ? i18next.t(`context.addTiers.modifyTiers`)
                : i18next.t(`context.addTiers.addTiers`)

            }
            customStyle={{
              width: '40%',
              borderRadius: '60px',
              zIndex: 2,
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={classes.root}>
      {contextModels && (
        <FormProvider {...formMethods}>
          <EAFormStepper
            stepsContent={stepsContent}
            onClose={close}
            onNext={() => {}}
            checkValidStep={checkValidStep}
            isFinalStep={false}
            getStepSetter={getStepSetter}
          />
        </FormProvider>
      )}
    </div>
  );
};

export default AddContext;
